import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";

/**
 * ShareCard renders a full-bleed, static, screenshot-ready promotional
 * card for a single book in one of three formats:
 *   - post      1080x1080  (Instagram square post)
 *   - story     1080x1920  (Instagram / Facebook story)
 *   - pinterest 1000x1500  (Pinterest 2:3 pin)
 *
 * Route: `/share/:format/:id`. No navbar/footer — this is captured by a
 * headless browser to produce a static JPG stored in /public/share/.
 */
const FORMATS = {
  post:      { w: 1080, h: 1080 },
  instagram: { w: 1080, h: 1080 },  // legacy alias
  story:     { w: 1080, h: 1920 },
  pinterest: { w: 1000, h: 1500 },
  bubble:    { w: 1080, h: 1080 },  // round profile-pic / story-highlight
};

const CONFETTI = [
  ["#FF6B9D", "#6EC4E8", "#FFD93D", "#FF8C42", "#B49FFF", "#6EC4E8"],
];

function BrandRow({ compact = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: compact ? 48 : 56,
          height: compact ? 48 : 56,
          borderRadius: 18,
          background: "#FF6B9D",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 12px 26px rgba(224,122,95,0.25)",
          fontFamily: "'Fredoka', sans-serif",
          fontSize: compact ? 24 : 28,
          fontWeight: 600,
        }}
      >
        ♥
      </div>
      <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: compact ? 24 : 26, fontWeight: 600, color: "#2D3055" }}>
        MKBunchBooks<span style={{ color: "#FF6B9D" }}>.com</span>
      </div>
    </div>
  );
}

function PostLayout({ book }) {
  return (
    <>
      <div style={{ position: "absolute", top: 44, left: 60, right: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BrandRow />
        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 30, color: "#FF6B9D" }}>new picture book</div>
      </div>
      <div style={{
        position: "absolute", left: 76, top: 176, width: 420, height: 525, borderRadius: 22, overflow: "hidden",
        transform: "rotate(-3.5deg)",
        boxShadow: "0 40px 90px rgba(45,48,85,0.28), 0 8px 22px rgba(45,48,85,0.14)", background: "#fff",
      }}>
        <img src={book.cover_image_url} alt={book.title} crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      <div style={{ position: "absolute", left: 560, top: 200, right: 60, bottom: 200, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 40, color: "#FF6B9D", lineHeight: 1 }}>coming {book.publish_date || "soon"}</div>
        <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 96, lineHeight: 1.02, margin: "12px 0 0 0", letterSpacing: "-0.02em", color: "#2D3055" }}>
          {book.title}
        </h1>
        {book.subtitle && (
          <p style={{ fontFamily: "'Caveat', cursive", fontSize: 44, color: "#6EC4E8", lineHeight: 1.1, margin: "18px 0 0 0" }}>{book.subtitle}</p>
        )}
        {book.age_range && (
          <div style={{ marginTop: 26 }}>
            <span style={{ display: "inline-block", padding: "10px 22px", background: "#FFFFFF", border: "1.5px solid #F0D9E8", borderRadius: 999, fontFamily: "'Fredoka', sans-serif", fontSize: 22, color: "#2D3055" }}>
              {book.age_range} · a whimsical picture book
            </span>
          </div>
        )}
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, bottom: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 20, color: "#7E82A1", letterSpacing: "0.14em", textTransform: "uppercase" }}>written by</div>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 44, color: "#2D3055", lineHeight: 1, marginTop: 6 }}>M.K. Bunch</div>
        </div>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, color: "#2D3055", background: "#FFFFFF", border: "1.5px solid #F0D9E8", padding: "12px 22px", borderRadius: 999 }}>mkbunchbooks.com</div>
      </div>
    </>
  );
}

function StoryLayout({ book }) {
  return (
    <>
      <div style={{ position: "absolute", top: 80, left: 60, right: 60, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <BrandRow />
        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 44, color: "#FF6B9D", marginTop: 18 }}>a whimsical new picture book</div>
      </div>
      <div style={{
        position: "absolute", left: 190, top: 320, width: 700, height: 875, borderRadius: 28, overflow: "hidden",
        transform: "rotate(-2.5deg)",
        boxShadow: "0 50px 100px rgba(45,48,85,0.30), 0 8px 22px rgba(45,48,85,0.14)", background: "#fff",
      }}>
        <img src={book.cover_image_url} alt={book.title} crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, top: 1260, textAlign: "center" }}>
        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 48, color: "#FF6B9D", lineHeight: 1 }}>coming {book.publish_date || "soon"}</div>
        <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 120, lineHeight: 1.02, margin: "14px 0 0 0", letterSpacing: "-0.02em", color: "#2D3055" }}>
          {book.title}
        </h1>
        {book.subtitle && (
          <p style={{ fontFamily: "'Caveat', cursive", fontSize: 52, color: "#6EC4E8", lineHeight: 1.1, margin: "20px 0 0 0" }}>{book.subtitle}</p>
        )}
        {book.age_range && (
          <div style={{ marginTop: 30 }}>
            <span style={{ display: "inline-block", padding: "12px 28px", background: "#FFFFFF", border: "1.5px solid #F0D9E8", borderRadius: 999, fontFamily: "'Fredoka', sans-serif", fontSize: 26, color: "#2D3055" }}>
              {book.age_range}
            </span>
          </div>
        )}
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 80, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, color: "#FFFFFF", background: "#FF6B9D", padding: "16px 34px", borderRadius: 999, boxShadow: "0 14px 28px rgba(255,107,157,0.35)" }}>
          link in bio → mkbunchbooks.com
        </div>
        <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 22, color: "#7E82A1" }}>by M.K. Bunch · Washington, D.C.</div>
      </div>
    </>
  );
}

function PinterestLayout({ book }) {
  return (
    <>
      <div style={{ position: "absolute", top: 60, left: 60, right: 60, textAlign: "center" }}>
        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 44, color: "#FF6B9D" }}>a whimsical new picture book</div>
        <h1 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 96, lineHeight: 1.02, margin: "12px 0 0 0", letterSpacing: "-0.02em", color: "#2D3055" }}>
          {book.title}
        </h1>
        {book.subtitle && (
          <p style={{ fontFamily: "'Caveat', cursive", fontSize: 42, color: "#6EC4E8", lineHeight: 1.1, margin: "14px 0 0 0" }}>{book.subtitle}</p>
        )}
      </div>
      <div style={{
        position: "absolute", left: 140, top: 400, width: 720, height: 720, borderRadius: 24, overflow: "hidden",
        transform: "rotate(-2deg)",
        boxShadow: "0 40px 90px rgba(45,48,85,0.28), 0 8px 22px rgba(45,48,85,0.14)", background: "#fff",
      }}>
        <img src={book.cover_image_url} alt={book.title} crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>
      <div style={{ position: "absolute", left: 60, right: 60, bottom: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        {book.age_range && (
          <span style={{ display: "inline-block", padding: "12px 28px", background: "#FFFFFF", border: "1.5px solid #F0D9E8", borderRadius: 999, fontFamily: "'Fredoka', sans-serif", fontSize: 24, color: "#2D3055" }}>
            {book.age_range} · coming {book.publish_date || "soon"}
          </span>
        )}
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 32, color: "#2D3055" }}>M.K. Bunch</div>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, color: "#FF6B9D" }}>save · share · mkbunchbooks.com</div>
      </div>
    </>
  );
}

function BubbleLayout({ book }) {
  return (
    <>
      {/* Two decorative concentric rings */}
      <div style={{
        position: "absolute", left: 30, top: 30, right: 30, bottom: 30, borderRadius: 999,
        border: "6px solid #FFFFFF",
        boxShadow: "inset 0 0 0 8px #FF6B9D",
      }} />
      {/* Circular book cover — dominant subject */}
      <div style={{
        position: "absolute", left: 190, top: 130, width: 700, height: 700, borderRadius: 999,
        overflow: "hidden",
        boxShadow: "0 30px 60px rgba(45,48,85,0.28)",
        background: "#fff",
        border: "10px solid #FFFFFF",
      }}>
        <img src={book.cover_image_url} alt={book.title} crossOrigin="anonymous"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transform: "scale(1.06)" }} />
      </div>
      {/* Bottom label */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 90, textAlign: "center" }}>
        <div style={{
          display: "inline-block",
          padding: "12px 26px",
          background: "#FFFFFF",
          border: "2px solid #FF6B9D",
          borderRadius: 999,
          fontFamily: "'Fredoka', sans-serif",
          fontSize: 30,
          color: "#2D3055",
          boxShadow: "0 12px 26px rgba(45,48,85,0.14)",
        }}>
          MKBunchBooks<span style={{ color: "#FF6B9D" }}>.com</span>
        </div>
      </div>
    </>
  );
}


export default function ShareCard() {
  const { format = "post", id } = useParams();
  const size = FORMATS[format] || FORMATS.post;
  const [book, setBook] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data)).catch(() => setErr("Book not found"));
  }, [id]);

  if (err) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>{err}</div>;
  if (!book) return null;

  const [c1, c2, c3, c4, c5, c6] = CONFETTI[0];
  const dotY = size.h - 260;

  return (
    <div
      data-testid="share-card"
      data-format={format}
      style={{
        width: size.w,
        height: size.h,
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(120% 90% at 15% 10%, #FFE1EF 0%, transparent 55%)," +
          "radial-gradient(120% 90% at 85% 90%, #EEE1FF 0%, transparent 55%)," +
          "linear-gradient(135deg, #FFF3F8 0%, #FFF9E8 100%)",
        color: "#2D3055",
        fontFamily: "'Nunito', sans-serif",
      }}
    >
      <div aria-hidden style={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(rgba(255,107,157,0.10) 1.2px, transparent 1.2px)",
        backgroundSize: "22px 22px", opacity: 0.6,
      }} />
      {[
        [Math.round(size.w * 0.13), Math.round(size.h * 0.75), c1],
        [Math.round(size.w * 0.48), Math.round(size.h * 0.65), c2],
        [Math.round(size.w * 0.07), Math.round(size.h * 0.37), c3],
        [Math.round(size.w * 0.91), Math.round(size.h * 0.31), c4],
        [Math.round(size.w * 0.88), Math.round(size.h * 0.76), c5],
        [Math.round(size.w * 0.46), Math.round(size.h * 0.28), c6],
      ].map(([x, y, col], i) => (
        <div key={i} style={{ position: "absolute", left: x, top: y, width: 14, height: 14, borderRadius: 999, background: col, opacity: 0.8 }} />
      ))}

      {format === "story" && <StoryLayout book={book} />}
      {format === "pinterest" && <PinterestLayout book={book} />}
      {format === "bubble" && <BubbleLayout book={book} />}
      {(format === "post" || format === "instagram") && <PostLayout book={book} />}

      <span style={{ position: "absolute", left: -1, top: dotY, width: 1, height: 1 }} aria-hidden />
    </div>
  );
}
